import { page, router } from "@inertiajs/svelte"

// Conversion events for Plausible. The server queues one at the moment a
// conversion happens (TracksAnalyticsEvents) and the next Inertia page
// carries it as the `analytics_events` prop; this fires it through the
// Plausible script mounted in layouts/application.html.erb, which queues
// calls made before it has loaded. Names and props must match the goals
// configured on stats.tinycloud.io.
export interface AnalyticsEvent {
  id: string
  name: string
  props?: Record<string, string>
  // Major units, net of VAT. Plausible converts into the goal's currency.
  revenue?: { currency: string; amount: number }
}

declare global {
  interface Window {
    plausible?: (
      name: string,
      options?: {
        props?: AnalyticsEvent["props"]
        revenue?: AnalyticsEvent["revenue"]
      },
    ) => void
  }
}

// The server hands an event out once, but the props it rode in on come back
// on every partial reload of that page and on history navigation to it, so
// each id fires once per app launch.
const fired = new Set<string>()

export function trackAnalyticsEvents(props: Record<string, unknown>): void {
  const events = (props.analytics_events as AnalyticsEvent[] | undefined) ?? []
  for (const event of events) {
    if (fired.has(event.id)) continue
    fired.add(event.id)
    window.plausible?.(event.name, {
      props: event.props,
      revenue: event.revenue,
    })
  }
}

// A press reviewer's goals that the browser sees for itself, with no redirect
// for the server's queue to ride on. The script's event-* attributes add
// account_type and outlet. See docs/features/plausible-goals.md.
export function trackReviewerGoal(name: string): void {
  if (page.props.auth.user?.reviewer) window.plausible?.(name)
}

// Registered before the app boots: Inertia fires `navigate` for the initial
// page as well as for every visit, so one listener sees every page's props.
export function initAnalyticsEvents(): void {
  router.on("navigate", (event) => {
    trackAnalyticsEvents(event.detail.page.props)
  })
}
